import { CalendarDays } from 'lucide-react';
import { Avatar } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { Profile } from '@/types';
import { ProfileBannerSurface } from '@/components/theme/ProfileBannerSurface';
import { gradientTextStyle } from '@/components/theme/CommunityThemeScope';
import { avatarFrameStyle } from '@/features/studio/model/profileDoc';
import type { Emblem, TitleCard } from '../cosmetics';
import { joinDate, stripHandle } from '../helpers';
import { useProfileTheme } from '../hooks/useProfileTheme';
import { EmblemGlyph } from './EmblemGlyph';

interface ProfilePreviewCardProps {
  profile: Profile;
  title: TitleCard;
  emblem: Emblem;
}

/** Fractal-noise overlay used by animated 'grain' titles (OG profile.css:16). */
const GRAIN_BG =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E\")";

/**
 * The profile hero card — OG `.profile-preview`. Banner comes from the user's
 * published WINR Studio design when one exists, otherwise the equipped
 * title-card art (with its animation). Accent/frame/name styling honor the
 * published profile customization.
 */
export function ProfilePreviewCard({ profile, title, emblem }: ProfilePreviewCardProps) {
  const name = profile.display_name ?? 'WINR';
  const handle = stripHandle(profile.handle);
  const since = joinDate(profile.created_at);
  const theme = useProfileTheme(profile.id, emblem.glow);
  const accent = theme.accent;

  return (
    <div className="relative mb-6 h-[200px] w-full overflow-hidden rounded-lg border border-card-line">
      <ProfileBannerSurface
        theme={theme}
        fallbackImage={title.bg}
        fallbackClassName={cn(title.anim === 'kenBurns' && 'animate-ken-burns')}
      >
        {/* gradientDrift radial sheen (legacy title art only) */}
        {!theme.hasCustomBanner && title.anim === 'gradientDrift' && (
          <div
            className="absolute inset-0 animate-gradient-drift"
            style={{
              background:
                'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.15), transparent 60%)',
              backgroundSize: '200% 200%',
            }}
          />
        )}
        {/* grain flicker (legacy title art only) */}
        {!theme.hasCustomBanner && title.anim === 'grain' && (
          <div
            className="pointer-events-none absolute inset-0 animate-grain opacity-40 mix-blend-overlay"
            style={{ backgroundImage: GRAIN_BG, backgroundSize: '128px' }}
          />
        )}
      </ProfileBannerSurface>
      {/* bottom-up legibility fade */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent from-40% to-[rgba(8,8,10,0.9)]" />

      {/* bottom cluster — identity + emblem, then the WINR meta beneath the title
          chip. Kept in the dark-faded bottom band so the accent text stays legible. */}
      <div className="absolute inset-x-5 bottom-4 z-[1]">
        <div className="flex items-end justify-between gap-3">
          {/* identity */}
          <div className="flex min-w-0 items-end gap-3">
            <Avatar
              src={profile.avatar_url}
              name={name}
              size={52}
              className="border-2"
              style={avatarFrameStyle(theme.doc.frame, accent)}
            />
            <div className="min-w-0">
              <div
                className="truncate text-lg font-extrabold leading-tight text-hi"
                style={theme.doc.nameStyle === 'gradient' ? gradientTextStyle(accent) : undefined}
              >
                {name}
              </div>
              {handle && (
                <div className="truncate text-[12px] font-semibold" style={{ color: accent }}>
                  @{handle}
                </div>
              )}
              <span
                className="mt-1 inline-block rounded-pill border px-2 py-0.5 text-[11px] font-semibold"
                style={{
                  color: accent,
                  borderColor: `color-mix(in srgb, ${accent} 30%, transparent)`,
                  background: `color-mix(in srgb, ${accent} 15%, transparent)`,
                }}
              >
                {title.name}
              </span>
            </div>
          </div>

          {/* emblem */}
          {theme.doc.showEmblem && (
            <div className="flex-none animate-breathe">
              <EmblemGlyph emblem={emblem} size={28} />
            </div>
          )}
        </div>

        {/* Member-since — below the title chip, aligned under the text column
            (avatar 52px + gap 12px = 64px → pl-16). */}
        {since && (
          <div
            className="mt-1.5 inline-flex items-center gap-1.5 pl-16 text-[11px] font-semibold drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
            style={{ color: accent }}
          >
            <CalendarDays size={12} /> WINR since {since}
          </div>
        )}
      </div>
    </div>
  );
}
